import { handleAttachmentPaste } from "utils/attachmentClipboard";

/** One place that says how a ticket status reads and what can be done from it. */

export const TICKET_STATUS = {
  OPEN: { label: "Open", color: "success" },
  RESOLVED: { label: "Resolved", color: "info" },
  CANCELLED: { label: "Cancelled", color: "default" },
  LOCKED: { label: "Locked", color: "warning" },
};

export const statusLabel = (status) => TICKET_STATUS[status]?.label || status || "Unknown";
export const statusColor = (status) => TICKET_STATUS[status]?.color || "default";

const ACTIONS = {
  OPEN: { label: "Reopen ticket", color: "primary" },
  RESOLVED: { label: "Mark resolved", color: "success" },
  CANCELLED: { label: "Cancel ticket", color: "error" },
  LOCKED: { label: "Lock ticket", color: "warning" },
};

/** The server says which moves the viewer may make (nextStatuses); this only dresses them. */
export const statusActions = (nextStatuses) =>
  (nextStatuses || []).filter((to) => ACTIONS[to]).map((to) => ({ to, ...ACTIONS[to] }));

const VERBS = { OPEN: "reopened", RESOLVED: "resolved", CANCELLED: "cancelled", LOCKED: "locked" };

/** "Asha resolved the ticket", or "Referral Test opened the ticket" for the first entry. */
export const statusChangeText = (entry) => {
  const who = entry?.authorName || (entry?.side === "OFFICE" ? "The office" : "The partner");
  const verb = entry?.fromStatus ? VERBS[entry.toStatus] || "changed" : "opened";
  return `${who} ${verb} the ticket`;
};

/** Client-side mirror of the server's attachment rules, so a bad file is caught before upload. */
export const ATTACHMENT_RULES = {
  maxFiles: 5,
  maxBytes: 10 * 1024 * 1024,
  types: ["image/png", "image/jpeg", "image/gif", "image/webp", "application/pdf"],
};

export const attachmentProblem = (files) => {
  if (files.length > ATTACHMENT_RULES.maxFiles) {
    return `Attach at most ${ATTACHMENT_RULES.maxFiles} files at a time.`;
  }
  const wrongType = files.find((file) => !ATTACHMENT_RULES.types.includes(file.type));
  if (wrongType) return `${wrongType.name} is not an image or PDF.`;
  const tooBig = files.find((file) => file.size > ATTACHMENT_RULES.maxBytes);
  if (tooBig) return `${tooBig.name} is larger than 10 MB.`;
  return null;
};

/** Files added by picking or pasting, or the reason they cannot be added. */
export const withAddedFiles = (files, added) => {
  const next = [...files, ...added];
  const problem = attachmentProblem(next);
  return problem ? { next: files, problem } : { next, problem: null };
};

/** onPaste for a ticket's text box: pasted screenshots and files join the attachments. */
export const pasteTicketFiles = (event, { files, onChange, onProblem }) =>
  handleAttachmentPaste(event, {
    onFiles: (added) => {
      const { next, problem } = withAddedFiles(files, added);
      if (problem) onProblem?.(problem);
      else onChange(next);
    },
  });

/** The multipart body both ticket-opening and replying send. */
export const ticketFormData = (fields, files) => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value != null && value !== "") form.append(key, value);
  });
  (files || []).forEach((file) => form.append("files", file));
  return form;
};
